import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTickerApi } from './useTickerApi'
import {
  PRESETS, TOKENS, DEFAULT_GAP, DEFAULT_MAX_ITEMS, DEFAULT_CHAR_BUDGET,
  packRows, renderRow, renderResult, rowLength, separatorLength,
  truncateToBudget,
} from './tickerFormat'

// Game results → ticker rows, ready to paste into the ticker sheet.
//
// Everything after the fetch is client-side: the operator is tuning wording and
// line width against what the crawl actually renders, and a round trip per
// keystroke would make that unusable.

const SETTINGS_KEY = 'tbtv.ticker.results.settings'

const DEFAULTS = {
  template: PRESETS[0].template,
  budget: DEFAULT_CHAR_BUDGET,
  maxItems: DEFAULT_MAX_ITEMS,
  gap: DEFAULT_GAP,
  trailing: false,
  mode: 'plain',
  trim: false,
  limit: 200,
}

const loadSettings = () => {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    return raw ? { ...DEFAULTS, ...JSON.parse(raw) } : { ...DEFAULTS }
  } catch { return { ...DEFAULTS } }
}

const isoDaysAgo = (n) => {
  const d = new Date()
  d.setDate(d.getDate() - n)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

const fmtDate = (iso) => {
  if (!iso) return ''
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

export default function GameResults() {
  const api = useTickerApi()
  const [seasons, setSeasons] = useState([])
  const [seasonId, setSeasonId] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [games, setGames] = useState([])
  const [excluded, setExcluded] = useState(() => new Set())
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(null)
  const [coverage, setCoverage] = useState(null)
  const [scope, setScope] = useState(null)
  const [fetching, setFetching] = useState(null)
  const [cfg, setCfg] = useState(loadSettings)
  const templateRef = useRef(null)

  const set = (patch) => setCfg(prev => {
    const next = { ...prev, ...patch }
    try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)) } catch { /* private mode */ }
    return next
  })

  useEffect(() => {
    api.listSeasons()
      .then(r => {
        const list = r?.seasons || []
        setSeasons(list)
        setSeasonId(prev => prev || (list[0]?.id ? String(list[0].id) : ''))
      })
      .catch(e => setError(e.message))
  }, [api])

  const load = useCallback(() => {
    setLoading(true); setError(null)
    api.listResults({ season: seasonId, from, to, limit: cfg.limit })
      .then(r => { setGames(r?.games || []); setScope(r?.scope || null); setExcluded(new Set()) })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false))
  }, [api, seasonId, from, to, cfg.limit])

  useEffect(() => { if (seasonId) load() }, [seasonId, load])

  useEffect(() => {
    if (!seasonId) return
    api.boxStatus(seasonId).then(setCoverage).catch(() => setCoverage(null))
  }, [api, seasonId, games])

  // Box scores only exist for games that already carry a final score, and scores
  // only arrive via a sync. Syncing before puck drop records none, so a night's
  // games stay invisible until the season is synced again — which is the whole
  // reason these buttons do the sync themselves rather than leaving it as a
  // separate step somebody has to remember in another module.
  const ensureScores = async () => {
    const fresh = await api.freshness(seasonId)
    if (!fresh?.unscored_past) return 0
    setFetching(`Syncing schedule — ${fresh.unscored_past} played game${fresh.unscored_past === 1 ? '' : 's'} with no score…`)
    await api.syncSeason(seasonId)
    return fresh.unscored_past
  }

  // Scrape the games that have a final score but no box score yet. The endpoint
  // caps each call at 40 pages, so walk batches until one comes back empty —
  // mid-season that is a few hundred games on first run.
  const fetchMissing = async () => {
    setError(null)
    let total = 0
    try {
      const synced = await ensureScores()
      for (let batch = 0; batch < 15; batch++) {
        setFetching(`Fetching box scores… ${total} so far`)
        const r = await api.fetchBoxScores(seasonId, { limit: 40 })
        total += r?.fetched || 0
        setCoverage({ finished: r?.finished, with_box: r?.with_box, missing: r?.missing })
        if (!r?.fetched) break
      }
      setFetching(total
        ? `Fetched ${total} box score${total === 1 ? '' : 's'}${synced ? ' (after syncing the schedule)' : ''}`
        : (synced ? 'Schedule synced — no box scores published yet' : 'Nothing new to fetch'))
      load()
    } catch (e) {
      setError(e.message)
      setFetching(null)
      return
    }
    setTimeout(() => setFetching(null), 3000)
  }

  // Re-pull box scores we already have — leagues correct stats after the fact,
  // which is why they're stored rather than fetched live. Scoped to a date range
  // because a bare refresh re-pulls the OLDEST finished games first, useless once
  // the season runs to hundreds of games and you want last night's corrections.
  const refetch = async () => {
    setError(null)
    const since = from || isoDaysAgo(7)
    try {
      await ensureScores()
      setFetching('Re-fetching…')
      const r = await api.fetchBoxScores(seasonId, { limit: 40, refresh: true, from: since, to: to || undefined })
      setCoverage({ finished: r?.finished, with_box: r?.with_box, missing: r?.missing })
      setFetching(`Re-fetched ${r?.fetched || 0} box score${r?.fetched === 1 ? '' : 's'}`)
      load()
    } catch (e) {
      setError(e.message); setFetching(null); return
    }
    setTimeout(() => setFetching(null), 3000)
  }

  // One item per included game, in the order the ticker will read them.
  const items = useMemo(() => games
    .filter(g => !excluded.has(g.game_id))
    .map(g => {
      const text = renderResult(cfg.template, g)
      return { game: g, text: cfg.trim ? truncateToBudget(text, cfg.budget) : text }
    }), [games, excluded, cfg.template, cfg.trim, cfg.budget])

  const rows = useMemo(() => {
    const packed = packRows(items.map(i => i.text), cfg)
    return packed.map(row => ({ ...row, rendered: renderRow(row, cfg) }))
  }, [items, cfg])

  const output = rows.map(r => r.rendered).join('\n')
  const sepLen = separatorLength(cfg.gap)
  const overCount = rows.filter(r => r.over).length

  // Say what is actually true of the selected range. Four states get confused
  // easily: games not played yet (normal), games being played now (on the ticker
  // already, as live scores), played games with no score (the schedule needs a
  // sync), and scored games with no box score (ready to fetch). Only the third
  // is a problem, and the button on this page fixes it — pointing elsewhere is
  // what sent the operator to the wrong module.
  const live = scope?.in_progress || 0
  const blocker = (() => {
    if (!scope || scope.games === 0) return null
    if (scope.unscored_past > 0) {
      return {
        tone: '#e2a24a',
        text: `${scope.unscored_past} played game${scope.unscored_past === 1 ? '' : 's'} in this range `
          + `${scope.unscored_past === 1 ? 'has' : 'have'} no final score yet`
          + (scope.last_synced_at ? ` (schedule last synced ${scope.last_synced_at.slice(0, 16)} UTC)` : '')
          + '. Click Fetch box scores — it syncs the schedule first, then pulls them.',
      }
    }
    // A live game has a score and no box score, and no amount of fetching will
    // give it one — the league publishes the box when the scorekeeper finalises.
    // Counting it as fetchable would send the operator after it all night.
    const missing = scope.scored - scope.with_box - live
    if (missing > 0) {
      return { tone: '#4a90e2', text: `${missing} game${missing === 1 ? '' : 's'} in this range `
        + `${missing === 1 ? 'has' : 'have'} a final score but no box score yet — click Fetch box scores.`
        + (live ? ` (${live} more ${live === 1 ? 'is' : 'are'} still being played.)` : '') }
    }
    if (live > 0) {
      return { tone: '#3fa66a', muted: true,
        text: `${live} game${live === 1 ? '' : 's'} in this range `
          + `${live === 1 ? 'is' : 'are'} being played now, showing the running score. `
          + 'The league does not publish the period, and scorers arrive with the box score once it is final.' }
    }
    if (scope.upcoming > 0 && scope.with_box === 0) {
      return { tone: '#4a90e2', muted: true,
        text: `${scope.upcoming} game${scope.upcoming === 1 ? '' : 's'} in this range `
          + `${scope.upcoming === 1 ? 'has' : 'have'} not been played yet.` }
    }
    return null
  })()

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(label); setTimeout(() => setCopied(null), 1800)
    } catch { setError('Clipboard blocked — select the text and copy manually.') }
  }

  const insertToken = (key) => {
    const el = templateRef.current
    const token = `<${key}>`
    if (!el) { set({ template: `${cfg.template}${token}` }); return }
    const { selectionStart: a, selectionEnd: b } = el
    set({ template: cfg.template.slice(0, a) + token + cfg.template.slice(b) })
    requestAnimationFrame(() => {
      el.focus()
      el.setSelectionRange(a + token.length, a + token.length)
    })
  }

  const toggle = (id) => setExcluded(prev => {
    const next = new Set(prev)
    next.has(id) ? next.delete(id) : next.add(id)
    return next
  })

  return (
    <div>
      {/* ---- source ---- */}
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex',
        gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Season</span>
          <select value={seasonId} onChange={e => setSeasonId(e.target.value)}>
            {seasons.length === 0 && <option value="">No seasons</option>}
            {seasons.map(s => (
              <option key={s.id} value={s.id}>{s.league} — {s.label}</option>
            ))}
          </select>
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>From</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>To</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)} />
        </label>
        <button onClick={load} disabled={loading || !seasonId}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <span className="muted" style={{ fontSize: 12 }}>
          {games.length} game{games.length === 1 ? '' : 's'} with a box score
          {excluded.size > 0 && ` · ${excluded.size} excluded`}
        </span>
        <span style={{ flex: 1 }} />
        {coverage && coverage.finished > 0 && (
          <span className="muted" style={{ fontSize: 12 }}>
            {coverage.with_box} of {coverage.finished} played games have one
            {coverage.missing > 0 && ` · ${coverage.missing} missing`}
          </span>
        )}
        {coverage && coverage.finished === 0 && (
          <span style={{ fontSize: 12, color: '#e2a24a' }}>
            No played games have final scores yet — Fetch box scores syncs the schedule first
          </span>
        )}
        <button onClick={fetchMissing} disabled={!seasonId || !!fetching}
          title="Sync the schedule if played games are missing final scores, then scrape any box scores that aren't stored yet">
          {fetching || 'Fetch box scores'}
        </button>
        {coverage && coverage.with_box > 0 && (
          <button onClick={refetch} disabled={!seasonId || !!fetching}
            title={`Sync the schedule if needed, then re-pull box scores for games since ${from || 'the last 7 days'} — picks up stat corrections the league made after the game`}>
            Re-fetch
          </button>
        )}
      </div>

      {error && (
        <div className="card" style={{ padding: 10, marginBottom: 12,
          borderLeft: '3px solid #e2574a' }}>{error}</div>
      )}

      {blocker && (
        <div className="card" style={{ padding: 10, marginBottom: 12, fontSize: 13,
          borderLeft: `3px solid ${blocker.tone}` }}>{blocker.text}</div>
      )}

      {/* ---- line format ---- */}
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center',
          marginBottom: 10 }}>
          <strong style={{ fontSize: 13 }}>Line text</strong>
          {PRESETS.map(p => (
            <button key={p.id} onClick={() => set({ template: p.template })}
              className={cfg.template === p.template ? 'btn-active' : ''}
              style={{
                fontSize: 12, padding: '3px 9px',
                border: '1px solid var(--border-color, #2a2a2a)', borderRadius: 12,
                background: cfg.template === p.template ? 'var(--accent, #4a90e2)' : 'transparent',
                color: cfg.template === p.template ? '#fff' : 'var(--text-muted, #888)',
                cursor: 'pointer',
              }}>{p.label}</button>
          ))}
        </div>

        <textarea ref={templateRef} value={cfg.template} rows={2}
          onChange={e => set({ template: e.target.value })}
          style={{ width: '100%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13 }} />

        <p className="muted" style={{ fontSize: 11, margin: '6px 0 0' }}>
          Click a token to insert it. Wrap a phrase in <code>[square brackets]</code> to drop it
          when a token inside has no value — a pulled goalie or an unpublished stat then leaves
          no dangling words.
        </p>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
          {TOKENS.map(t => (
            <button key={t.key} onClick={() => insertToken(t.key)} title={t.hint}
              style={{
                fontSize: 11, padding: '2px 7px', borderRadius: 10,
                border: '1px solid var(--border-color, #2a2a2a)', background: 'transparent',
                color: 'var(--text-muted, #888)', cursor: 'pointer',
                fontFamily: 'ui-monospace, Menlo, monospace',
              }}>{`<${t.key}>`}</button>
          ))}
        </div>
      </div>

      {/* ---- width + packing ---- */}
      <div className="card" style={{ padding: 12, marginBottom: 12, display: 'flex',
        gap: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Characters per line</span>
          <input type="number" min="20" max="600" value={cfg.budget} style={{ width: 90 }}
            onChange={e => set({ budget: Number(e.target.value) || DEFAULT_CHAR_BUDGET })} />
        </label>
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Max items per line</span>
          <input type="number" min="1" max="12" value={cfg.maxItems} style={{ width: 70 }}
            onChange={e => set({ maxItems: Math.max(1, Number(e.target.value) || 1) })} />
        </label>
        {cfg.maxItems > 1 && (
          <>
            <label style={{ display: 'grid', gap: 4 }}>
              <span className="muted" style={{ fontSize: 12 }}>Gap (nbsp each side)</span>
              <input type="number" min="0" max="40" value={cfg.gap} style={{ width: 70 }}
                onChange={e => set({ gap: Math.max(0, Number(e.target.value) || 0) })} />
            </label>
            <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
              <input type="checkbox" checked={cfg.trailing}
                onChange={e => set({ trailing: e.target.checked })} />
              Trailing separator
            </label>
          </>
        )}
        <label style={{ display: 'grid', gap: 4 }}>
          <span className="muted" style={{ fontSize: 12 }}>Output</span>
          <select value={cfg.mode} onChange={e => set({ mode: e.target.value })}>
            <option value="plain">Plain text</option>
            <option value="formula">Sheets formula</option>
          </select>
        </label>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12 }}>
          <input type="checkbox" checked={cfg.trim}
            onChange={e => set({ trim: e.target.checked })} />
          Trim over-long lines
        </label>
        <div className="muted" style={{ fontSize: 11, maxWidth: 420, lineHeight: 1.45 }}>
          {cfg.maxItems > 1
            ? `Each join costs ${sepLen} characters (${cfg.gap} nbsp · "|" · ${cfg.gap} nbsp), counted against the budget. Use the Sheets formula output — plain nbsp can be normalized away on paste.`
            : `One line per game, full width. ${DEFAULT_CHAR_BUDGET} is what the crawl is verified to render; raise it here to test longer lines.`}
        </div>
      </div>

      {/* ---- output ---- */}
      <div className="card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 8 }}>
          <strong style={{ fontSize: 13 }}>
            {rows.length} ticker row{rows.length === 1 ? '' : 's'}
          </strong>
          {overCount > 0 && (
            <span style={{ fontSize: 12, color: '#e2a24a' }}>
              {overCount} over {cfg.budget} characters
            </span>
          )}
          <span style={{ flex: 1 }} />
          <button onClick={() => copy(output, 'all')} disabled={!rows.length}>
            {copied === 'all' ? 'Copied' : 'Copy all rows'}
          </button>
          <button onClick={() => {
            const blob = new Blob([output], { type: 'text/plain;charset=utf-8' })
            const a = document.createElement('a')
            a.href = URL.createObjectURL(blob)
            a.download = `ticker-results-${new Date().toISOString().slice(0, 10)}.txt`
            a.click(); URL.revokeObjectURL(a.href)
          }} disabled={!rows.length}>Download .txt</button>
        </div>
        <p className="muted" style={{ fontSize: 11, marginTop: 0 }}>
          Paste into the ticker sheet — one row per line, each landing in its own cell.
        </p>
        <textarea readOnly value={output} rows={Math.min(12, Math.max(4, rows.length + 1))}
          style={{ width: '100%', fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 12 }} />
      </div>

      {/* ---- per-game preview ---- */}
      <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--border-color, #2a2a2a)' }}>
              <th style={{ padding: '8px 10px', width: 34 }}></th>
              <th style={{ padding: '8px 10px', width: 70 }}>Date</th>
              <th style={{ padding: '8px 10px', width: 52 }}>Div</th>
              <th style={{ padding: '8px 10px' }}>Ticker line</th>
              <th style={{ padding: '8px 10px', width: 56, textAlign: 'right' }}>Chars</th>
            </tr>
          </thead>
          <tbody>
            {games.map(g => {
              const off = excluded.has(g.game_id)
              const text = renderResult(cfg.template, g)
              const shown = cfg.trim ? truncateToBudget(text, cfg.budget) : text
              const len = rowLength([shown], { gap: cfg.gap })
              return (
                <tr key={g.game_id} style={{
                  borderBottom: '1px solid var(--border-color, #1f1f1f)',
                  opacity: off ? 0.4 : 1,
                }}>
                  <td style={{ padding: '6px 10px' }}>
                    <input type="checkbox" checked={!off} onChange={() => toggle(g.game_id)} />
                  </td>
                  <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }} className="muted">
                    {g.in_progress
                      ? <span style={{ color: '#3fa66a', fontWeight: 600 }} title="Being played now — running score, no scorers until the box score is final">LIVE</span>
                      : fmtDate(g.date)}
                  </td>
                  <td style={{ padding: '6px 10px' }} className="muted">{g.division}</td>
                  <td style={{ padding: '6px 10px', fontFamily: 'ui-monospace, Menlo, monospace' }}>
                    {shown}
                  </td>
                  <td style={{
                    padding: '6px 10px', textAlign: 'right',
                    color: len > cfg.budget ? '#e2a24a' : 'var(--text-muted, #888)',
                  }}>{len}</td>
                </tr>
              )
            })}
            {games.length === 0 && !loading && (
              <tr><td colSpan={5} style={{ padding: 16 }} className="muted">
                {blocker ? blocker.text
                  : scope && scope.games === 0 ? 'No games scheduled in this date range.'
                  : 'No games with a box score in range. Use Fetch box scores above.'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
